'use client';

import { useEffect, useState, memo } from 'react';

import { useTranslations } from 'next-intl';
import { createPortal } from 'react-dom';

import {
  Spinner,
  type T_SpinnerProps
} from '@/components/ui/shadcn-io/spinner';
import { cn } from '@/lib/utils/cn';

export interface I_LoadingSpinnerProps extends T_SpinnerProps {
  /** Loading text to display below the spinner */
  text?: string;
  /** Size of the spinner */
  size?: number;
  /** Whether to show the spinner in a centered container */
  centered?: boolean;
  /** Whether to show the spinner in a full screen overlay */
  overlay?: boolean;
  /** Custom className for the container */
  containerClassName?: string;
  /** Whether to show a backdrop blur effect */
  backdrop?: boolean;
  textClassName?: string;
}

/**
 * LoadingSpinner component with multiple variants and layout options
 * Perfect for form submissions, data fetching, and page transitions
 */
export const LoadingSpinner = memo(function LoadingSpinner({
  variant = 'circle',
  text,
  size = 24,
  centered = false,
  overlay = false,
  containerClassName,
  backdrop = false,
  className,
  textClassName,
  ...props
}: I_LoadingSpinnerProps) {
  const spinnerElement = (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2',
        centered && 'h-full w-full',
        containerClassName
      )}
    >
      <Spinner
        variant={variant}
        size={size}
        className={cn('text-primary', className)}
        {...props}
      />
      {text && (
        <p
          className={cn(
            'text-sm text-muted-foreground font-medium',
            textClassName
          )}
        >
          {text}
        </p>
      )}
    </div>
  );

  /* Use Portal for overlay to ensure it's always above everything */
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (overlay && mounted) {
    return createPortal(
      <div
        className={cn(
          'fixed inset-0 z-[9999] flex items-center justify-center',
          backdrop && 'backdrop-blur-sm bg-background/80',
          !backdrop && 'bg-background/95'
        )}
      >
        {spinnerElement}
      </div>,
      document.body
    );
  }

  // Fallback if not mounted yet (SSR)
  if (overlay) return null;

  return spinnerElement;
});

LoadingSpinner.displayName = 'LoadingSpinner';

// Convenience components for common use cases
export const PageLoader = memo(function PageLoader({
  text,
  ...props
}: Omit<I_LoadingSpinnerProps, 'centered' | 'overlay'>) {
  return (
    <LoadingSpinner
      variant="circle"
      text={text}
      centered
      size={32}
      {...props}
    />
  );
});

export const ButtonLoader = memo(function ButtonLoader({
  size = 16,
  ...props
}: Omit<I_LoadingSpinnerProps, 'centered' | 'overlay' | 'text'>) {
  return <LoadingSpinner variant="circle" size={size} {...props} />;
});

export const FormLoader = memo(function FormLoader({
  text,
  ...props
}: Omit<I_LoadingSpinnerProps, 'centered' | 'overlay'>) {
  const t = useTranslations('notifications');
  const displayText = text ?? t('saving');

  return (
    <LoadingSpinner
      variant="circle"
      text={displayText}
      centered
      size={24}
      {...props}
    />
  );
});

export const OverlayLoader = memo(function OverlayLoader({
  text,
  backdrop = true,
  ...props
}: Omit<I_LoadingSpinnerProps, 'centered'>) {
  return (
    <LoadingSpinner
      variant="circle"
      text={text}
      overlay
      backdrop={backdrop}
      size={32}
      {...props}
    />
  );
});

// Export all spinner variants for direct use
export { Spinner } from '@/components/ui/shadcn-io/spinner';
export type { T_SpinnerProps as SpinnerProps } from '@/components/ui/shadcn-io/spinner';
